import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { admin as adminAPI } from '../../services/api';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmModal from '../Common/ConfirmModal';
import './AdminDashboard.css';

/**
 * AdminDashboard - Admin panel for platform statistics
 *
 * Protected by JWT auth + admin role check on backend
 * Access via: /admin (requires Google login + admin role in database)
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [topConsumersTokens, setTopConsumersTokens] = useState([]);
  const [topConsumersTime, setTopConsumersTime] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // User management state
  const [searchEmail, setSearchEmail] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [freeMonths, setFreeMonths] = useState(1);
  const [grantReason, setGrantReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Confirmation modal
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      setError('Please log in to access the admin panel');
      setLoading(false);
      return;
    }

    fetchAllData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchAllData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);

      const [statsRes, tokensRes, timeRes, activityRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getTopConsumersTokens(10),
        adminAPI.getTopConsumersTime(10),
        adminAPI.getRecentActivity(50),
      ]);

      setStats(statsRes.data);
      setTopConsumersTokens(Array.isArray(tokensRes.data) ? tokensRes.data : []);
      setTopConsumersTime(Array.isArray(timeRes.data) ? timeRes.data : []);
      setRecentActivity(Array.isArray(activityRes.data) ? activityRes.data : []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);

      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else if (err.response?.status === 403) {
        setAccessDenied(true);
        setError('Access Denied: You do not have administrator privileges');
      } else {
        setError('Failed to load admin data');
      }
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  const formatCurrency = (cents) => {
    return '$' + (cents / 100).toFixed(2);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleSearchUser = async (e) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;

    setSearchLoading(true);
    setSearchError(null);
    setSearchedUser(null);
    setActionSuccess(null);

    try {
      const res = await adminAPI.searchUser(searchEmail.trim());
      setSearchedUser(res.data);
      setSearchError(null);
    } catch (err) {
      console.error('Failed to search user:', err);
      if (err.response?.status === 404) {
        setSearchError('User not found');
      } else {
        setSearchError('Failed to search user');
      }
      setSearchedUser(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleGrantLifetimeFree = async () => {
    if (!searchedUser) return;

    const confirmed = await confirm({
      title: 'Grant Lifetime Free Access',
      message: `Are you sure you want to grant lifetime free access to ${searchedUser.email}?`,
      confirmText: 'Grant Access',
      confirmStyle: 'primary'
    });

    if (!confirmed) return;

    setActionLoading(true);
    setActionSuccess(null);

    try {
      await adminAPI.grantLifetimeFree(searchedUser.userId, grantReason);
      setActionSuccess('Lifetime free access granted successfully!');
      // Refresh user data
      const res = await adminAPI.searchUser(searchedUser.email);
      setSearchedUser(res.data);
      setGrantReason('');
    } catch (err) {
      console.error('Failed to grant lifetime free:', err);
      setSearchError('Failed to grant lifetime free access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGrantFreeMonths = async () => {
    if (!searchedUser || freeMonths < 1) return;

    const confirmed = await confirm({
      title: 'Grant Free Months',
      message: `Are you sure you want to grant ${freeMonths} free month${freeMonths > 1 ? 's' : ''} to ${searchedUser.email}?`,
      confirmText: 'Grant Months',
      confirmStyle: 'primary'
    });

    if (!confirmed) return;

    setActionLoading(true);
    setActionSuccess(null);

    try {
      await adminAPI.grantFreeMonths(searchedUser.userId, freeMonths, grantReason);
      setActionSuccess(`Granted ${freeMonths} free month(s) successfully!`);
      // Refresh user data
      const res = await adminAPI.searchUser(searchedUser.email);
      setSearchedUser(res.data);
      setGrantReason('');
      setFreeMonths(1);
    } catch (err) {
      console.error('Failed to grant free months:', err);
      setSearchError('Failed to grant free months');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetRole = async (role) => {
    if (!searchedUser) return;
    const hasRole = searchedUser.roles && searchedUser.roles.includes(role);

    const confirmed = await confirm({
      title: `${hasRole ? 'Remove' : 'Add'} ${role} Role`,
      message: `Are you sure you want to ${hasRole ? 'remove' : 'add'} the ${role} role ${hasRole ? 'from' : 'to'} ${searchedUser.email}?`,
      confirmText: hasRole ? 'Remove Role' : 'Add Role',
      confirmStyle: hasRole ? 'danger' : 'primary'
    });

    if (!confirmed) return;

    setActionLoading(true);
    setActionSuccess(null);
    try {
      const res = await adminAPI.toggleRole(searchedUser.userId, role);
      setActionSuccess(`${hasRole ? 'Removed' : 'Added'} ${role} role successfully!`);

      // Update the searched user with new roles
      setSearchedUser({
        ...searchedUser,
        roles: res.data.roles
      });
    } catch (err) {
      console.error('Failed to toggle role:', err);
      setSearchError(`Failed to toggle ${role} role`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!searchedUser) return;

    const confirmed = await confirm({
      title: 'Delete User',
      message: `Are you sure you want to PERMANENTLY DELETE ${searchedUser.email}? This will delete all their data including languages, flashcards, transcriptions, and conversations. This action CANNOT be undone.`,
      confirmText: 'Delete User',
      confirmStyle: 'danger'
    });

    if (!confirmed) return;

    setActionLoading(true);
    setActionSuccess(null);

    try {
      await adminAPI.deleteUser(searchedUser.userId, grantReason);
      setActionSuccess(`User ${searchedUser.email} has been permanently deleted.`);
      // Clear the searched user
      setSearchedUser(null);
      setSearchEmail('');
      setGrantReason('');
    } catch (err) {
      console.error('Failed to delete user:', err);
      setSearchError('Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  // Not logged in
  if (!user) {
    return (
      <div className="admin-error">
        <h1>🔒 Authentication Required</h1>
        <p>Please log in with your administrator account to access the admin panel.</p>
        <button onClick={() => navigate('/login')}>Go to Login</button>
      </div>
    );
  }

  // Access denied (logged in but not admin)
  if (accessDenied) {
    return (
      <div className="admin-error">
        <h1>🚫 Access Denied</h1>
        <p>You do not have administrator privileges.</p>
        <p className="user-info">Logged in as: <strong>{user.name}</strong> ({user.email})</p>
        <button onClick={() => navigate('/')}>Back to Home</button>
      </div>
    );
  }

  // Loading
  if (loading && !stats) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  // Error
  if (error && !accessDenied) {
    return (
      <div className="admin-error">
        <h1>Error</h1>
        <p>{error}</p>
        <button onClick={fetchAllData}>Retry</button>
        <button onClick={() => navigate('/')}>Back to Home</button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <h1>🔒 Admin Dashboard</h1>
        <div className="admin-user-info">
          <span>👤 {user.name}</span>
        </div>
        <div className="admin-actions">
          <button onClick={fetchAllData} className="btn-refresh">
            🔄 Refresh
          </button>
          <span className="last-updated">
            Auto-refresh: 30s
          </span>
        </div>
      </header>

      {/* Overview Stats */}
      <section className="stats-overview">
        <h2>Platform Overview</h2>
        <div className="stats-grid">
          <StatCard
            title="Students"
            value={formatNumber(stats?.studentUsers || 0)}
            subtitle={`${stats?.activeUsers || 0} active (30d)`}
            icon="🎓"
          />
          <StatCard
            title="Teachers"
            value={formatNumber(stats?.teacherUsers || 0)}
            subtitle={`${stats?.totalUsers || 0} total users`}
            icon="👨‍🏫"
          />
          <StatCard
            title="Premium Users"
            value={formatNumber(stats?.premiumUsers || 0)}
            subtitle={`${stats?.trialUsers || 0} on trial`}
            icon="⭐"
          />
          <StatCard
            title="Conversations"
            value={formatNumber(stats?.totalConversations || 0)}
            subtitle={`${formatNumber(stats?.totalMessages || 0)} messages`}
            icon="💬"
          />
          <StatCard
            title="AI Tokens"
            value={formatNumber(stats?.totalTokensConsumed || 0)}
            subtitle={formatCurrency(stats?.totalCostUSD * 100 || 0)}
            icon="🤖"
          />
          <StatCard
            title="Meetings"
            value={formatNumber(stats?.totalMeetings || 0)}
            subtitle={`${stats?.activeMeetings || 0} active now`}
            icon="📹"
          />
          <StatCard
            title="Flashcards"
            value={formatNumber(stats?.totalFlashcards || 0)}
            subtitle="Created"
            icon="📚"
          />
          <StatCard
            title="Revenue"
            value={formatCurrency(stats?.totalRevenue * 100 || 0)}
            subtitle={`MRR: ${formatCurrency(stats?.monthlyRecurring * 100 || 0)}`}
            icon="💰"
          />
          <StatCard
            title="Transcriptions"
            value={formatNumber(stats?.totalTranscriptions || 0)}
            subtitle="Processed"
            icon="📝"
          />
        </div>
      </section>

      {/* User Management */}
      <section className="user-management">
        <h2>User Management</h2>

        {/* Search Form */}
        <form onSubmit={handleSearchUser} className="user-search-form">
          <input
            type="email"
            placeholder="Enter user email to search..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="search-input"
          />
          <button type="submit" disabled={searchLoading} className="btn-search">
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
        </form>

        {/* Search Error */}
        {searchError && (
          <div className="search-error">
            {searchError}
          </div>
        )}

        {/* Action Success */}
        {actionSuccess && (
          <div className="action-success">
            {actionSuccess}
          </div>
        )}

        {/* User Details */}
        {searchedUser && (
          <div className="user-details">
            <div className="user-info-section">
              <h3>User Information</h3>
              <div className="user-info-grid">
                <div className="info-item">
                  <label>Name:</label>
                  <span>{searchedUser.name || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{searchedUser.email}</span>
                </div>
                <div className="info-item">
                  <label>User ID:</label>
                  <span className="user-id">{searchedUser.userId}</span>
                </div>
                <div className="info-item">
                  <label>Roles:</label>
                  <span className="user-roles">
                    {searchedUser.roles && searchedUser.roles.length > 0
                      ? searchedUser.roles.map((role, idx) => (
                          <span
                            key={idx}
                            className={`role-badge ${role}`}
                          >
                            {role}
                          </span>
                        ))
                      : 'None'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Created:</label>
                  <span>{new Date(searchedUser.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="info-item">
                  <label>Last Active:</label>
                  <span>
                    {searchedUser.lastActiveAt && searchedUser.lastActiveAt.Valid
                      ? new Date(searchedUser.lastActiveAt.Time).toLocaleDateString()
                      : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            <div className="role-management-section">
              <h3>Role Management</h3>
              <p className="role-subtitle">Toggle roles - user can have both Student and Teacher roles</p>
              <div className="role-switch-container">
                <button
                  onClick={() => handleSetRole('student')}
                  disabled={actionLoading}
                  className={searchedUser.roles && searchedUser.roles.includes('student') ? 'role-option active' : 'role-option'}
                >
                  {searchedUser.roles && searchedUser.roles.includes('student') ? '✓ Student' : 'Student'}
                </button>
                <button
                  onClick={() => handleSetRole('teacher')}
                  disabled={actionLoading}
                  className={searchedUser.roles && searchedUser.roles.includes('teacher') ? 'role-option active' : 'role-option'}
                >
                  {searchedUser.roles && searchedUser.roles.includes('teacher') ? '✓ Teacher' : 'Teacher'}
                </button>
              </div>
            </div>

            <div className="subscription-info-section">
              <h3>Subscription Status</h3>
              <div className="subscription-badges">
                <span className={searchedUser.hasPremium ? 'badge premium' : 'badge free'}>
                  {searchedUser.hasPremium ? 'Premium' : 'Free'}
                </span>
                {searchedUser.hasLifetimePremium && (
                  <span className="badge lifetime">Lifetime Free</span>
                )}
                {searchedUser.trialEndsAt && new Date(searchedUser.trialEndsAt.Time) > new Date() && (
                  <span className="badge trial">
                    Trial until {new Date(searchedUser.trialEndsAt.Time).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <div className="actions-section">
              <h3>Grant Access</h3>

              <div className="grant-reason">
                <label>Reason (optional):</label>
                <input
                  type="text"
                  placeholder="e.g., Early supporter, Bug reporter, etc."
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="reason-input"
                />
              </div>

              <div className="action-buttons">
                <div className="action-group">
                  <button
                    onClick={handleGrantLifetimeFree}
                    disabled={actionLoading || searchedUser.hasLifetimePremium}
                    className="btn-lifetime-free"
                  >
                    {searchedUser.hasLifetimePremium
                      ? 'Already Lifetime Free'
                      : actionLoading ? 'Processing...' : 'Grant Lifetime Free'}
                  </button>
                </div>

                <div className="action-group">
                  <div className="months-selector">
                    <label>Free Months:</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={freeMonths}
                      onChange={(e) => setFreeMonths(parseInt(e.target.value) || 1)}
                      className="months-input"
                    />
                  </div>
                  <button
                    onClick={handleGrantFreeMonths}
                    disabled={actionLoading}
                    className="btn-grant-months"
                  >
                    {actionLoading ? 'Processing...' : `Grant ${freeMonths} Month${freeMonths > 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>

              <div className="danger-zone">
                <h4 style={{ color: '#d32f2f', marginBottom: '12px' }}>Danger Zone</h4>
                <button
                  onClick={handleDeleteUser}
                  disabled={actionLoading}
                  className="btn-delete-user"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#d32f2f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.5 : 1
                  }}
                >
                  {actionLoading ? 'Processing...' : 'Delete User Permanently'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Top Consumers */}
      <div className="top-consumers-section">
        <section className="top-consumers">
          <h2>🔥 Top Consumers by AI Tokens</h2>
          <table className="consumers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Tokens</th>
                <th>Cost</th>
                <th>Messages</th>
                <th>Convos</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {topConsumersTokens.length > 0 ? (
                topConsumersTokens.map((user, idx) => (
                  <tr key={user.userId}>
                    <td>{idx + 1}</td>
                    <td>
                      <div className="user-info">
                        <strong>{user.name}</strong>
                        <small>{user.email}</small>
                      </div>
                    </td>
                    <td>{formatNumber(user.totalTokens)}</td>
                    <td>{formatCurrency(user.costUsd * 100)}</td>
                    <td>{formatNumber(user.totalMessages)}</td>
                    <td>{user.totalConversations}</td>
                    <td>
                      <span className={user.hasPremium ? 'badge premium' : 'badge free'}>
                        {user.hasPremium ? 'Premium' : 'Free'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-data">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="top-consumers">
          <h2>⏱️ Top Consumers by Chat Time</h2>
          <table className="consumers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Time</th>
                <th>Tokens</th>
                <th>Messages</th>
                <th>Convos</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {topConsumersTime.length > 0 ? (
                topConsumersTime.map((user, idx) => (
                  <tr key={user.userId}>
                    <td>{idx + 1}</td>
                    <td>
                      <div className="user-info">
                        <strong>{user.name}</strong>
                        <small>{user.email}</small>
                      </div>
                    </td>
                    <td>{formatTime(user.totalConversationMinutes)}</td>
                    <td>{formatNumber(user.totalTokens)}</td>
                    <td>{formatNumber(user.totalMessages)}</td>
                    <td>{user.totalConversations}</td>
                    <td>
                      <span className={user.hasPremium ? 'badge premium' : 'badge free'}>
                        {user.hasPremium ? 'Premium' : 'Free'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-data">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {/* Recent Activity */}
      <section className="recent-activity">
        <h2>📊 Recent Activity</h2>
        <div className="activity-list">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-type">{activity.type}</div>
                <div className="activity-details">
                  <strong>{activity.userEmail}</strong>
                  <p>{activity.description}</p>
                </div>
                <div className="activity-time">
                  {new Date(activity.createdAt).toLocaleString()}
                </div>
              </div>
            ))
          ) : (
            <p className="no-data">No recent activity</p>
          )}
        </div>
      </section>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmStyle={confirmState.confirmStyle}
      />
    </div>
  );
};

// StatCard Component
const StatCard = ({ title, value, subtitle, icon }) => {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <h3>{title}</h3>
        <div className="stat-value">{value}</div>
        <div className="stat-subtitle">{subtitle}</div>
      </div>
    </div>
  );
};

export default AdminDashboard;
